import { Box, Button } from "@mui/material";
import { buttonFileStyle } from "shared/lib/style";
import style from "./loadFile.module.scss";
import fileIcon from "assets/images/myDocument/file.svg";
import btnIcon from "assets/images/myDocument/btn_icon.svg";
import trashRedImg from "assets/images/myDocument/trash.svg";
import {
  UseFormSetValue,
  useFormContext,
  useController
} from "react-hook-form";
import { ICreateMarathon } from "features/createEditMarathon/model/types";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorMessage } from "shared/ui/ErrorMessage/ErrorMessage";
import { toast } from "react-toastify";
interface ILoadDocumentProps {
  setValue: UseFormSetValue<ICreateMarathon>;
  languageId: number;
  error?: string;
  indexLanguage: number;
}

export const LoadImage: React.FC<ILoadDocumentProps> = ({
  languageId,
  error,
  indexLanguage,
}) => {
  const { t } = useTranslation();
  const [isDragOpen, setIsDragOpen] = useState(false);
  const [preview, setPreview] = useState<string>("");
  const [fileError, setFileError] = useState<string>("");
  const blobRef = useRef<string | null>(null);
  const hydratedFromUrlRef = useRef<string | null>(null);

  const { formState, resetField, control } = useFormContext<ICreateMarathon>();
  const { errors } = formState as any;

  const { field: logoField } = useController({
    control,
    name: `translations.${indexLanguage}.logo`,
    defaultValue: null,
  });
  const logo = logoField.value;

  function getFileBase(): string {
    const serverUrl = process.env.REACT_APP_SERVER_URL || "";
    const defaultUrl = process.env.REACT_APP_DEFAULT_IMAGE_URL || "https://marathon-app.timelysoft.org";
    const base = serverUrl.replace(/\/api(\/v1)?\/?$/i, "");
    return base || defaultUrl;
  }

  function joinUrl(base: string, path: string) {
    const b = (base || "").replace(/\/+$/, "");
    const p = (path || "").replace(/^\/+/, "");
    const encoded = p.split("/").map((s) => encodeURIComponent(s)).join("/");
    return `${b}/${encoded}`;
  }

  function toAbsolute(url: string) {
    if (/^(https?:|blob:|data:)/i.test(url)) return encodeURI(url);
    const base = getFileBase();
    if (url.startsWith("/staticfiles/") || url.startsWith("/app/")) return `${base}${url}`;
    if (url.startsWith("staticfiles/") || url.startsWith("app/")) return `${base}/${url}`;
    return `${base}/${url.replace(/^\/+/, "")}`;
  }

  function toProxyPath(abs: string) {
    try {
      const u = new URL(abs);
      return `/staticfiles${u.pathname.replace(/^\/staticfiles/, "")}`;
    } catch {
      return abs;
    }
  }

  function filenameFromUrl(abs: string, contentType?: string): string {
    try {
      const u = new URL(abs);
      const raw = decodeURIComponent(u.pathname.split("/").pop() || "");
      if (raw) return raw;
    } catch { }
    const extFromType =
      contentType?.includes("png") ? "png" :
        contentType?.includes("webp") ? "webp" :
          contentType?.includes("gif") ? "gif" :
            contentType?.includes("jpeg") ? "jpg" : "bin";
    return `image.${extFromType}`;
  }

  async function fetchAsFile(abs: string): Promise<File | null> {
    for (const creds of [undefined, "include" as const]) {
      try {
        const r = await fetch(abs, creds ? { credentials: creds as any, cache: "no-store" } : { cache: "no-store" });
        if (!r.ok) continue;
        const ct = r.headers.get("content-type") || "";
        if (!/^image\//i.test(ct)) continue;
        const blob = await r.blob();
        const name = filenameFromUrl(abs, blob.type || ct);
        return new File([blob], name, { type: blob.type || ct });
      } catch {
      }
    }

    try {
      const proxied = toProxyPath(abs);
      const r2 = await fetch(proxied, { cache: "no-store" });
      if (r2.ok) {
        const ct2 = r2.headers.get("content-type") || "";
        if (/^image\//i.test(ct2)) {
          const blob2 = await r2.blob();
          const name2 = filenameFromUrl(abs, blob2.type || ct2);
          return new File([blob2], name2, { type: blob2.type || ct2 });
        }
      }
    } catch { }
    return null;
  }

  const validateFile = (file: File): string | null => {
    const allowedMime = ["image/png", "image/jpeg", "image/svg+xml"];
    const allowedExt = ["png", "jpg", "jpeg", "svg"];

    // Проверка формата файла
    const mimeOk = !file.type || allowedMime.includes(file.type);
    const ext = file.name.split(".").pop()?.toLowerCase();
    const extOk = !!ext && allowedExt.includes(ext);

    if (!mimeOk && !extOk) {
      return "Разрешены только файлы формата PNG, JPEG, JPG, SVG";
    }

    // Проверка размера файла
    if (file.size > 20 * 1024 * 1024) {
      return "Размер файла не должен превышать 20 МБ";
    }

    return null;
  };

  const handleFileChange = (file: File) => {
    const error = validateFile(file);
    setFileError(error || "");

    if (!error) {
      hydratedFromUrlRef.current = null;
      logoField.onChange(file);
      toast.success("Файл успешно загружен");
    }
  };

  const handleLogoDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileChange(file);
      setIsDragOpen(false);
    }
  };

  useEffect(() => {
    if (blobRef.current) {
      try { URL.revokeObjectURL(blobRef.current); } catch { }
      blobRef.current = null;
    }

    if (!logo) {
      hydratedFromUrlRef.current = null;
      setPreview("");
      return;
    }

    if (logo instanceof File) {
      const u = URL.createObjectURL(logo);
      blobRef.current = u;
      setPreview(u);
      return;
    }

    const raw =
      typeof logo === "string" ? logo :
        (logo as any)?.url || (logo as any)?.path;

    if (!raw || typeof raw !== "string") {
      setPreview("");
      return;
    }

    const abs = toAbsolute(raw);
    const withBuster = `${abs}${abs.includes("?") ? "&" : "?"}t=${Date.now()}`;
    setPreview(withBuster);

    if (hydratedFromUrlRef.current === abs) return;

    let cancelled = false;
    (async () => {
      const file = await fetchAsFile(abs);
      if (!file || cancelled) return;
      hydratedFromUrlRef.current = abs;
      logoField.onChange(file);
    })();

    return () => { cancelled = true; };
  }, [logo]);

  useEffect(() => {
    return () => {
      if (blobRef.current) {
        try { URL.revokeObjectURL(blobRef.current); } catch { }
        blobRef.current = null;
      }
    };
  }, []);

  const handleDeleteLogo = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    hydratedFromUrlRef.current = null;
    if (blobRef.current) {
      try { URL.revokeObjectURL(blobRef.current); } catch { }
      blobRef.current = null;
    }
    setPreview("");
    setFileError("");
    logoField.onChange(null);
    resetField(`translations.${indexLanguage}.logo`, { defaultValue: null });
  };

  // Получаем все ошибки для отображения
  const getErrorMessage = () => {
    const formError = errors.translations?.[indexLanguage]?.logo?.message;
    return fileError || formError || "";
  };

  return (
    <Box>
      <label className={`label-personalCabinet ${style.label}`} htmlFor="-">
        {t("createMarathon.img")}

        {logo ? (
          <div
            className={style.box_logo}
          >
            {preview ? (
              <img
                key={preview}
                className={style.box_logo_item}
                src={preview}
                alt={t("createMarathon.marathonLogo")}
                crossOrigin="use-credentials"
                style={{ display: "block", maxWidth: "100%", height: "auto", objectFit: "contain" }}
                onError={(e) => {
                  e.currentTarget.title = "Ошибка загрузки изображения";
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  minHeight: 160,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  color: "#888",
                }}
              >
                {t("createMarathon.previewIsPreparing") || "Превью загружается..."}
              </div>
            )}

            <div className={style.delete_icon}>
              <img
                src={trashRedImg}
                alt={t("createMarathon.deteFile")}
                onClick={handleDeleteLogo}
              />
            </div>
          </div>
        ) : (
          <div
            className={`${style.box_load} ${isDragOpen ? style.box_load_drag : ""}`}
            style={{
              border: getErrorMessage() ? "2px solid #d4322a" : "",
              backgroundColor: getErrorMessage() ? "rgba(255,0,0,0.05)" : "",
            }}
            onDragEnter={(e) => { e.preventDefault(); setIsDragOpen(true); }}
            onDragOver={(e) => { e.preventDefault(); setIsDragOpen(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragOpen(false); }}
            onDrop={handleLogoDrop}
          >
            <img src={fileIcon} alt="Файл" />
            <div className={style.box_load_text}>
              {isDragOpen ? (
                <p>{t("createMarathon.dropToLoad")}</p>
              ) : (
                <>
                  <p>
                    {t("createMarathon.fileDrag1")} <br /> {t("createMarathon.fileDrag2")}
                  </p>
                  <Button
                    variant="contained"
                    component="label"
                    sx={buttonFileStyle}
                    className={style.box_load_btn}
                  >
                    <img src={btnIcon} alt="Выбрать файл" />
                    <span>{t("createMarathon.chooseFile")}</span>
                    <input
                      type="file"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleFileChange(f);
                      }}
                      accept="image/png, image/jpeg, image/jpg, image/svg+xml"
                      hidden
                    />
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </label>

      <Box sx={{ width: "100%" }}>
        <Box className="globalErrorMessage">{error}</Box>
        <ErrorMessage error={getErrorMessage()} />
      </Box>
    </Box>
  );
};

